/* ==========================================================================
   SKNBODY HOMEPAGE HERO
   File: assets/skn-home-hero.js

   Responsibilities:
   - Rotate the six brand words
   - Autoplay the muted looping desktop/mobile hero film when browser policy allows
   - Prefer reliable inline playback on iOS/in-app browsers
   - Pause offscreen for performance
   - Respect reduced-motion preferences
   - Support Shopify Theme Editor section reloads
   ========================================================================== */

(function () {
  'use strict';

  const HERO_SELECTOR = '[data-skn-home-hero]';

  if (window.SKNHomeHero) {
    window.SKNHomeHero.initAll(document);
    return;
  }

  const heroStates = new WeakMap();

  function getReducedMotionQuery() {
    return window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  function getNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function getStickyOffset() {
    const homeHeader = document.querySelector('[data-skn-home-header]');
    if (!homeHeader) return 12;
    return Math.round(homeHeader.getBoundingClientRect().height + 12);
  }

  function initHero(hero) {
    if (!hero || heroStates.has(hero)) return;

    const words = Array.from(hero.querySelectorAll('.skn-home-hero__word'));
    const video = hero.querySelector('[data-skn-hero-video]');
    const stage = hero.querySelector('.skn-home-hero__stage');
    const anchorLinks = Array.from(hero.querySelectorAll('[data-skn-hero-anchor]'));

    const wordInterval = Math.max(1000, getNumber(hero.dataset.wordInterval, 2000));
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const reducedMotionQuery = getReducedMotionQuery();

    let activeWordIndex = 0;
    let wordTimer = null;
    let intersectionObserver = null;
    let isVisible = true;
    let destroyed = false;
    let autoplayRetryTimers = [];

    function showWord(index) {
      if (!words.length) return;
      activeWordIndex = (index + words.length) % words.length;

      words.forEach(function (word, wordIndex) {
        word.classList.toggle('is-active', wordIndex === activeWordIndex);
      });
    }

    function stopWordRotation() {
      if (!wordTimer) return;
      window.clearInterval(wordTimer);
      wordTimer = null;
    }

    function startWordRotation() {
      stopWordRotation();

      if (destroyed || !isVisible || reducedMotionQuery.matches || words.length < 2) {
        return;
      }

      wordTimer = window.setInterval(function () {
        showWord(activeWordIndex + 1);
      }, wordInterval);
    }

    function prepareVideo() {
      if (!video) return;

      video.autoplay = true;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.disablePictureInPicture = true;

      video.setAttribute('autoplay', '');
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('preload', 'auto');
      video.removeAttribute('controls');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
      video.setAttribute('x-webkit-airplay', 'deny');
    }

    function clearAutoplayRetries() {
      autoplayRetryTimers.forEach(function (timer) {
        window.clearTimeout(timer);
      });
      autoplayRetryTimers = [];
    }

    function tryPlayVideo() {
      if (!video || destroyed || !isVisible || reducedMotionQuery.matches) {
        return Promise.resolve(false);
      }

      prepareVideo();

      let playPromise;
      try {
        playPromise = video.play();
      } catch (error) {
        return Promise.resolve(false);
      }

      if (playPromise && typeof playPromise.then === 'function') {
        return playPromise
          .then(function () {
            video.classList.add('is-playing');
            if (stage) stage.classList.add('is-playing');
            return true;
          })
          .catch(function () {
            return false;
          });
      }

      video.classList.add('is-playing');
      if (stage) stage.classList.add('is-playing');
      return Promise.resolve(true);
    }

    function scheduleAutoplayRetries() {
      clearAutoplayRetries();
      if (!video || reducedMotionQuery.matches || !isVisible) return;

      [0, 120, 350, 800, 1600].forEach(function (delay) {
        const timer = window.setTimeout(function () {
          if (!destroyed && isVisible && video.paused) {
            tryPlayVideo();
          }
        }, delay);
        autoplayRetryTimers.push(timer);
      });
    }

    function pauseVideo() {
      clearAutoplayRetries();
      if (video) video.pause();
    }

    function handlePlaying() {
      if (video) video.classList.add('is-playing');
      if (stage) stage.classList.add('is-playing');
    }

    function handleVideoError() {
      if (video) video.classList.remove('is-playing');
      if (stage) stage.classList.remove('is-playing');
    }

    function handleVisibilityChange(visible) {
      isVisible = visible;

      if (visible) {
        startWordRotation();
        scheduleAutoplayRetries();
      } else {
        stopWordRotation();
        pauseVideo();
      }
    }

    function handleViewportChange() {
      if (!video) return;

      prepareVideo();
      video.classList.remove('is-playing');
      if (stage) stage.classList.remove('is-playing');
      video.load();

      if (isVisible && !reducedMotionQuery.matches) {
        scheduleAutoplayRetries();
      }
    }

    function handleMotionPreferenceChange() {
      if (reducedMotionQuery.matches) {
        stopWordRotation();
        showWord(0);
        pauseVideo();
      } else if (isVisible) {
        startWordRotation();
        scheduleAutoplayRetries();
      }
    }

    function handlePageVisibility() {
      if (document.hidden) {
        pauseVideo();
      } else if (isVisible && !reducedMotionQuery.matches) {
        scheduleAutoplayRetries();
      }
    }

    function handlePageShow() {
      if (isVisible && !reducedMotionQuery.matches) {
        scheduleAutoplayRetries();
      }
    }

    /*
      iOS Low Power Mode and some in-app browsers can reject autoplay by OS
      policy. If that happens, the first normal interaction anywhere on the
      page starts the already-muted film. The customer does not need to find
      or tap the video itself.
    */
    function handleFirstInteraction() {
      if (video && video.paused && !reducedMotionQuery.matches) {
        tryPlayVideo();
      }
    }

    function handleAnchorClick(event) {
      const link = event.currentTarget;
      const href = link.getAttribute('href');

      if (!href || !href.startsWith('#') || href === '#') return;

      const target = document.querySelector(href);
      if (!target) return;

      event.preventDefault();

      const targetTop =
        target.getBoundingClientRect().top +
        window.pageYOffset -
        getStickyOffset();

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth'
      });

      if (history.pushState) {
        history.pushState(null, '', href);
      }
    }

    if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.target !== hero) return;
            handleVisibilityChange(entry.isIntersecting && entry.intersectionRatio > 0.02);
          });
        },
        { threshold: [0, 0.02, 0.12] }
      );

      intersectionObserver.observe(hero);
    }

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleViewportChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(handleViewportChange);
    }

    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', handleMotionPreferenceChange);
    } else if (typeof reducedMotionQuery.addListener === 'function') {
      reducedMotionQuery.addListener(handleMotionPreferenceChange);
    }

    if (video) {
      prepareVideo();
      video.addEventListener('loadedmetadata', scheduleAutoplayRetries);
      video.addEventListener('canplay', scheduleAutoplayRetries);
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('error', handleVideoError);
    }

    document.addEventListener('visibilitychange', handlePageVisibility);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('pointerdown', handleFirstInteraction, { passive: true });
    document.addEventListener('touchstart', handleFirstInteraction, { passive: true });

    anchorLinks.forEach(function (link) {
      link.addEventListener('click', handleAnchorClick);
    });

    showWord(0);

    if (reducedMotionQuery.matches) {
      pauseVideo();
    } else {
      startWordRotation();
      scheduleAutoplayRetries();
    }

    function destroy() {
      destroyed = true;
      stopWordRotation();
      clearAutoplayRetries();

      if (intersectionObserver) intersectionObserver.disconnect();

      if (video) {
        video.removeEventListener('loadedmetadata', scheduleAutoplayRetries);
        video.removeEventListener('canplay', scheduleAutoplayRetries);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('error', handleVideoError);
      }

      document.removeEventListener('visibilitychange', handlePageVisibility);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('pointerdown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);

      anchorLinks.forEach(function (link) {
        link.removeEventListener('click', handleAnchorClick);
      });

      if (typeof mobileQuery.removeEventListener === 'function') {
        mobileQuery.removeEventListener('change', handleViewportChange);
      } else if (typeof mobileQuery.removeListener === 'function') {
        mobileQuery.removeListener(handleViewportChange);
      }

      if (typeof reducedMotionQuery.removeEventListener === 'function') {
        reducedMotionQuery.removeEventListener('change', handleMotionPreferenceChange);
      } else if (typeof reducedMotionQuery.removeListener === 'function') {
        reducedMotionQuery.removeListener(handleMotionPreferenceChange);
      }

      heroStates.delete(hero);
    }

    heroStates.set(hero, { destroy: destroy });
  }

  function initAll(root) {
    const context = root || document;

    if (context.matches && context.matches(HERO_SELECTOR)) {
      initHero(context);
    }

    context.querySelectorAll(HERO_SELECTOR).forEach(initHero);
  }

  function destroyAll(root) {
    const context = root || document;
    const heroes = [];

    if (context.matches && context.matches(HERO_SELECTOR)) {
      heroes.push(context);
    }

    context.querySelectorAll(HERO_SELECTOR).forEach(function (hero) {
      heroes.push(hero);
    });

    heroes.forEach(function (hero) {
      const state = heroStates.get(hero);
      if (state && state.destroy) state.destroy();
    });
  }

  window.SKNHomeHero = {
    initAll: initAll,
    destroyAll: destroyAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAll(document);
      },
      { once: true }
    );
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target);
  });
})();
